/**
 * Backend LOCAL con estado para la sección de I+D. Ejecuta el flujo (abrir → evaluar →
 * rankear, y simular) de forma interactiva mientras el motor real no está hospedado en
 * la ventana. Implementa el MISMO contrato que expondrá el puente del preload, así que
 * al cablear el backend real la UI no cambia. Es un stand-in de presentación: usa una
 * heurística de scoring, no la lógica de dominio del motor.
 */
import type {
  ProductDevelopmentOverviewDto, PdCandidateDto, PdVerdict, PdIndexDto,
  OpenInvestigationInput, SimulateInput, SimulationResultDto, BusinessKpiDto,
} from "./types";

export const PD_INDEX_LABELS: readonly string[] = [
  "Demanda", "Competencia", "Riesgo", "Rentabilidad", "Importación", "Escalabilidad", "Logístico",
  "Marketing", "Tendencia", "Diferenciación", "Innovación", "Prob. éxito", "IA",
];
export const PD_INDEX_KEYS: readonly string[] = [
  "demand", "competition", "risk", "profitability", "importFeasibility", "scalability", "logistics",
  "marketing", "trend", "differentiation", "innovation", "successProbability", "ai",
];

export function buildIndices(values: readonly number[]): readonly PdIndexDto[] {
  return PD_INDEX_LABELS.map((label, i) => ({ key: PD_INDEX_KEYS[i] as string, label, value: values[i] ?? 0 }));
}

const clamp = (n: number): number => Math.max(0, Math.min(100, Math.round(n)));

/** Pesos del compuesto de probabilidad de éxito (mismo espíritu que ScoringService). */
const WEIGHTS = [14, 10, 12, 16, 7, 8, 6, 7, 9, 5, 3, 13];

/** Traduce la matriz de decisión (con compuerta de riesgo) a un veredicto. */
export function verdictFor(probability: number, risk: number): PdVerdict {
  if (risk < 40) return probability >= 45 ? "high_risk" : "no_sell";
  if (probability < 30) return "no_sell";
  if (probability < 45) return "high_risk";
  if (probability < 58) return "medium_risk";
  if (probability < 70) return "interesting";
  if (probability < 82) return "recommended";
  if (probability < 92) return "star";
  return "exceptional";
}

/** Calcula los 12 índices componentes + probabilidad de éxito desde la entrada. */
export function scoreFromInput(input: OpenInvestigationInput): { values: number[]; probability: number } {
  const demand = clamp(input.demand);
  const competition = clamp(100 - input.competition);
  const risk = clamp(100 - (input.leadTimeDays * 0.5 + (100 - input.aiConfidence) * 0.3));
  const profitability = clamp(input.marginPct);
  const importFeasibility = clamp(85 - input.leadTimeDays * 0.4);
  const scalability = clamp((input.demand + input.marginPct) / 2);
  const logistics = clamp(100 - input.leadTimeDays * 0.6);
  const marketing = clamp(input.demand * 0.7 + 20);
  const trend = 60;
  const differentiation = clamp(100 - input.competition * 0.7);
  const innovation = clamp(input.aiConfidence * 0.5 + 20);
  const ai = clamp(input.aiConfidence);

  const components = [demand, competition, risk, profitability, importFeasibility, scalability, logistics, marketing, trend, differentiation, innovation, ai];
  const totalWeight = WEIGHTS.reduce((a, b) => a + b, 0);
  const weighted = components.reduce((acc, v, i) => acc + v * (WEIGHTS[i] ?? 0), 0);
  const probability = clamp(weighted / totalWeight);
  return { values: [...components, probability], probability };
}

export class PdBackend {
  private candidates: PdCandidateDto[];
  private seq = 0;

  constructor(seed: readonly PdCandidateDto[] = []) {
    this.candidates = [...seed];
  }

  private kpis(): readonly BusinessKpiDto[] {
    const total = this.candidates.length;
    const winners = this.candidates.filter((c) => c.verdict === "star" || c.verdict === "exceptional").length;
    const winRate = total === 0 ? 0 : Math.round((winners / total) * 100);
    const opportunity = this.candidates.reduce((acc, c) => acc + c.monthlyUnits * (c.marginPct / 100) * 0.03, 0);
    return [
      { key: "investigations", label: "Investigaciones", value: String(total), deltaPct: 20, accent: "yellow", icon: "spark" },
      { key: "winners", label: "Ganadores", value: String(winners), deltaPct: 15, accent: "green", icon: "target" },
      { key: "winrate", label: "Tasa de éxito", value: `${winRate}%`, deltaPct: 4, accent: "blue", icon: "pulse" },
      { key: "opportunity", label: "Oportunidad estimada", value: `$${opportunity.toFixed(1)}M`, deltaPct: 9.5, accent: "yellow", icon: "bolt" },
    ];
  }

  overview(): ProductDevelopmentOverviewDto {
    const candidates = [...this.candidates].sort((a, b) => b.successProbability - a.successProbability);
    return { kpis: this.kpis(), candidates };
  }

  /** Abre una investigación, la evalúa y la agrega al ranking (devuelve el candidato). */
  open(input: OpenInvestigationInput): PdCandidateDto {
    const { values, probability } = scoreFromInput(input);
    const risk = values[2] ?? 0;
    const candidate: PdCandidateDto = {
      id: `cand-new-${++this.seq}`,
      name: input.name.trim(),
      category: input.category.trim(),
      verdict: verdictFor(probability, risk),
      successProbability: probability,
      monthlyUnits: Math.max(0, Math.round(input.demand * 4)),
      marginPct: clamp(input.marginPct),
      indices: buildIndices(values),
    };
    this.candidates = [candidate, ...this.candidates];
    return candidate;
  }

  /** Simula un cambio de precio y publicidad sobre un candidato existente. */
  simulate(input: SimulateInput): SimulationResultDto | null {
    const candidate = this.candidates.find((c) => c.id === input.candidateId);
    if (!candidate) return null;
    const base = candidate.successProbability;
    const result = clamp(
      base + input.priceDeltaPct * 0.12 - Math.abs(input.priceDeltaPct) * 0.04 +
      input.adSpendDeltaPct * 0.1 - Math.max(0, input.adSpendDeltaPct) * 0.03,
    );
    return { candidateId: candidate.id, baseProbability: base, resultProbability: result, delta: result - base };
  }
}
